import { mapGetters, mapState, mapActions } from 'vuex';
import { debounce } from 'lodash';
import appHeader from '@/components/app/app-header/app-header.vue';
import appSecondaryNav from '@/components/app/app-secondary-nav/app-secondary-nav.vue';
import appMainNav from '@/components/app/app-main-nav/app-main-nav.vue';
import appBottomNav from '@/components/app/app-bottom-nav/app-bottom-nav.vue';
import appMobileMenu from '@/components/app/app-mobile-menu/app-mobile-menu.vue';
import appFooter from '@/components/app/app-footer/app-footer.vue';
import notificationItems from '@/components/notification-items/notification-items.vue';
import appHeaderNotification from '@/components/app/app-header-notification/app-header-notification.vue';
import maintenanceNotification from '@/components/maintenance-notification/maintenance-notification.vue';
import searchBar from '@/components/app/app-header/search-bar/search-bar.vue';
import sideNotification from '@/components/generics/side-notification/side-notification.vue';
import constants from '@/constants';
import { isStandalone } from '@/mixins/userAgent';
import { scrollState } from '@/router';
import navBrowseCategoriesOverlay from '@/components/app/app-secondary-nav/nav-browse-categories-overlay/nav-browse-categories-overlay.vue';

const SIMPLE_LAYOUT_ROUTES = [
  'disqus-auth',
  'disqus-logout',
];

const DEBOUNCE_INTERVAL = 500;
const PINBOARD_SINGLE_MIN_WIDTH = 1459;
const PINBOARD_DUAL_MIN_WIDTH = 1894;


export default {
  components: {
    appHeader,
    appHeaderNotification,
    appSecondaryNav,
    appMainNav,
    appBottomNav,
    appFooter,
    appMobileMenu,
    maintenanceNotification,
    notificationItems,
    searchBar,
    sideNotification,
    navBrowseCategoriesOverlay,
    // eslint-disable-next-line global-require
    ...(process.env.PINNING_ENABLED ? { pinboardPanel: require('@/components/pinning/pinboard-panel/pinboard-panel.vue').default } : {}),
  },
  data() {
    return {
      isStandalone,
      isTransitioning: false,
      pinboardObserver: null,
      panelScrollHandler: null,
    };
  },
  watch: {
    $route() {
      setTimeout(() => {
        this.isTransitioning = false;
      }, 1000);
    },
  },
  methods: {
    ...mapActions('app', ['setPinboardModeVisible']),
    onBeforeLeave() {
      this.isTransitioning = true;
    },
    onAfterEnter() {
      this.isTransitioning = false;
      if (scrollState.pendingPosition) {
        const { x, y } = scrollState.pendingPosition;
        scrollState.pendingPosition = null;
        window.scrollTo(x, y);
      } else {
        window.scrollTo(0, 0);
      }
    },
  },
  mounted() {
    if (!this.isPinningEnabled) return;

    const update = debounce(() => {
      const w = window.innerWidth;
      this.setPinboardModeVisible({ single: w >= PINBOARD_SINGLE_MIN_WIDTH, dual: w >= PINBOARD_DUAL_MIN_WIDTH });
    }, DEBOUNCE_INTERVAL);

    this.pinboardObserver = new window.ResizeObserver(update);
    this.pinboardObserver.observe(document.documentElement);
    const w = window.innerWidth;
    this.setPinboardModeVisible({ single: w >= PINBOARD_SINGLE_MIN_WIDTH, dual: w >= PINBOARD_DUAL_MIN_WIDTH });

    const footer = this.$el.querySelector('.app-layout__footer');
    if (footer) {
      const updatePanelBottom = () => {
        const footerTop = footer.getBoundingClientRect().top;
        const bottom = Math.max(0, window.innerHeight - footerTop);
        this.$el.style.setProperty('--panel-bottom', `${bottom}px`);
      };
      this.panelScrollHandler = updatePanelBottom;
      window.addEventListener('scroll', updatePanelBottom, { passive: true });
      updatePanelBottom();
    }
  },
  beforeDestroy() {
    if (this.pinboardObserver) this.pinboardObserver.disconnect();
    if (this.panelScrollHandler) {
      window.removeEventListener('scroll', this.panelScrollHandler);
    }
  },
  computed: {
    ...mapGetters('app', ['hasBanner', 'hasMaintenanceMessage', 'isLayoutDesktop', 'isPinningEnabled', 'isPinboardSingleModeVisible', 'isPinboardDualModeVisible', 'sidebarVisible']),
    ...mapState('app', ['hasServerErrorOccurred', 'liveUpdatesDisabled', 'mobileMenuOpened']),
    ...mapState('popup', ['currentPopup', 'currentSmallPopup']),
    ...mapState('notifications', [
      'sideNotificationLevel',
      'sideNotificationText',
      'sideNotificationOnCloseCallback',
      'showNotificationsWidget',
    ]),
    ...mapGetters('app', ['isLayoutMobile']),
    ...mapGetters('user', ['isUserRestricted']),
    ...mapState('search', ['searchBarVisible']),
    isSimpleLayout() {
      return SIMPLE_LAYOUT_ROUTES.includes(this.$route.name);
    },
    appModifiers() {
      return {
        'app-layout': true,
        'app-layout--with-overflow': (
          (this.currentPopup !== null || this.currentSmallPopup !== null) && this.isTransitioning
        ),
        'app-layout--full-width': this.$route.meta.appContentFullWidth,
        'app-layout--bg-white': true,
        'app-layout--pinboard': this.isPinboardLayoutEnabled,
        'app-layout--single-left-open': this.showLeftPanel && this.inSinglePanelMode,
        'app-layout--dual-open': this.isPinboardDualModeVisible && this.sidebarVisible && this.isPinboardLayoutEnabled,
      };
    },
    headerModifiers() {
      return {
        'app-layout__header': true,
        'app-layout__header--secondary': true,
        'app-layout__header--error': (this.hasServerErrorOccurred || this.hasMaintenanceMessage || this.hasBanner || this.liveUpdatesDisabled),
        'app-layout__header--notifications-widget-open': this.showNotificationsWidget,
      };
    },
    contentModifiers() {
      return {
        'app-layout__content': true,
        'app-layout__content--secondary': !this.isSimpleLayout,
        'app-layout__content--simple': this.isSimpleLayout,
        'app-layout__content--error': (this.hasServerErrorOccurred || this.hasMaintenanceMessage || this.hasBanner || this.liveUpdatesDisabled),
        'app-layout__content--with-overflow': this.$route.meta.appContentOverflowed,
        'app-layout__content--full-width': this.$route.meta.appContentFullWidth,
        'app-layout__content--pinboard': this.isPinboardLayoutEnabled,
      };
    },
    sideNotificationShouldDisplay() {
      return this.sideNotificationLevel && this.sideNotificationText;
    },
    isPinboardLayoutEnabled() {
      return this.isPinningEnabled && ['home', 'trends', 'market-details'].includes(this.$route.name);
    },
    inSinglePanelMode() {
      return this.isPinboardSingleModeVisible && !this.isPinboardDualModeVisible;
    },
    showLeftPanel() {
      return (this.isPinboardDualModeVisible || (this.isPinboardSingleModeVisible && this.activePinboardConfig.singlePanelSide === 'left'))
        && this.isPinboardLayoutEnabled && this.sidebarVisible;
    },
    showRightPanel() {
      return (this.isPinboardDualModeVisible || (this.isPinboardSingleModeVisible && this.activePinboardConfig.singlePanelSide === 'right'))
        && this.isPinboardLayoutEnabled && this.sidebarVisible;
    },
    activePinboardConfig() {
      return { leftLabel: 'Widgets (Primary)', rightLabel: 'Widgets (Secondary)', singlePanelSide: 'left' };
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/app/app-layout/app-layout.js